import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { Check, Upload, ArrowRight, ArrowLeft, CheckCircle2, FileText } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { getContent, submitEnquiry } from '@/lib/api';

const STEPS = ['About You', 'Your Project', 'Requirement', 'Submit'];
const TIMELINES = ['Immediate', 'Within 1 month', '1–3 months', '3–6 months', 'Just exploring'];

const field = 'h-12 rounded-xl border-[#20272A]/15 bg-white text-[#20272A] placeholder:text-[#20272A]/35 focus-visible:ring-[#B88A3B]';

export const EnquiryForm = () => {
  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [fileName, setFileName] = useState('');
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '',
    projectLocation: '', requiredService: '', timeline: '',
    projectDescription: '', file: null,
  });

  useEffect(() => {
    getContent('services').then(setServices).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep = () => {
    if (step === 0) {
      if (!form.name.trim()) return 'Please enter your name.';
      if (!form.email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return 'Please enter a valid email.';
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) { set('file', f); setFileName(f.name); }
  };

  const submit = async () => {
    const err = validateStep();
    if (err) { toast.error(err); setStep(0); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'file') { if (v) fd.append('file', v); }
        else fd.append(k, v || '');
      });
      await submitEnquiry(fd);
      setDone(true);
      toast.success('Enquiry received. We will get back to you shortly.');
    } catch (e) {
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className='rounded-2xl border border-[#075B61]/15 bg-white p-10 text-center shadow-[0_18px_60px_rgba(32,39,42,0.10)]' data-testid='enquiry-success'>
        <div className='mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#075B61]/10 text-[#075B61]'>
          <CheckCircle2 size={34} />
        </div>
        <h3 className='mt-6 font-head text-2xl font-semibold text-[#20272A]'>Thank you, {form.name.split(' ')[0]}.</h3>
        <p className='mx-auto mt-3 max-w-md text-[#20272A]/65'>
          Your enquiry has been received. Our team will review your requirement and get back to you shortly.
        </p>
      </div>
    );
  }

  return (
    <div className='rounded-2xl border border-[#20272A]/10 bg-white p-6 sm:p-9 shadow-[0_18px_60px_rgba(32,39,42,0.10)]' data-testid='enquiry-form'>
      {/* progress */}
      <div className='mb-8'>
        <div className='flex items-center justify-between'>
          {STEPS.map((s, i) => (
            <div key={s} className='flex flex-1 items-center'>
              <div className='flex items-center gap-2'>
                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs font-medium transition-colors ${
                    i < step
                      ? 'border-[#075B61] bg-[#075B61] text-white'
                      : i === step
                      ? 'border-[#B88A3B] bg-[#B88A3B] text-[#20272A]'
                      : 'border-[#20272A]/20 text-[#20272A]/40'
                  }`}
                >
                  {i < step ? <Check size={14} /> : i + 1}
                </span>
                <span className={`hidden sm:block text-xs ${i === step ? 'text-[#20272A] font-medium' : 'text-[#20272A]/45'}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && (
                <span className={`mx-2 h-px flex-1 ${i < step ? 'bg-[#075B61]' : 'bg-[#20272A]/12'}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      <AnimatePresence mode='wait'>
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          {step === 0 && (
            <div className='grid gap-5 sm:grid-cols-2'>
              <div>
                <Label className='text-[#20272A]/70'>Name *</Label>
                <Input className={`mt-2 ${field}`} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder='Your full name' data-testid='enquiry-name' />
              </div>
              <div>
                <Label className='text-[#20272A]/70'>Company</Label>
                <Input className={`mt-2 ${field}`} value={form.company} onChange={(e) => set('company', e.target.value)} placeholder='Company / organisation' data-testid='enquiry-company' />
              </div>
              <div>
                <Label className='text-[#20272A]/70'>Email *</Label>
                <Input className={`mt-2 ${field}`} type='email' value={form.email} onChange={(e) => set('email', e.target.value)} placeholder='you@company.com' data-testid='enquiry-email' />
              </div>
              <div>
                <Label className='text-[#20272A]/70'>Phone</Label>
                <Input className={`mt-2 ${field}`} value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder='+91' data-testid='enquiry-phone' />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className='grid gap-5 sm:grid-cols-2'>
              <div>
                <Label className='text-[#20272A]/70'>Project Location</Label>
                <Input className={`mt-2 ${field}`} value={form.projectLocation} onChange={(e) => set('projectLocation', e.target.value)} placeholder='City / site location' data-testid='enquiry-location' />
              </div>
              <div>
                <Label className='text-[#20272A]/70'>Required Service</Label>
                <Select value={form.requiredService} onValueChange={(v) => set('requiredService', v)}>
                  <SelectTrigger className={`mt-2 ${field}`} data-testid='enquiry-service'>
                    <SelectValue placeholder='Select a service' />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((s) => (
                      <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>
                    ))}
                    <SelectItem value='Machinery Hiring'>Machinery Hiring</SelectItem>
                    <SelectItem value='Other'>Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className='sm:col-span-2'>
                <Label className='text-[#20272A]/70'>Timeline</Label>
                <Select value={form.timeline} onValueChange={(v) => set('timeline', v)}>
                  <SelectTrigger className={`mt-2 ${field}`} data-testid='enquiry-timeline'>
                    <SelectValue placeholder='Expected timeline' />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMELINES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className='grid gap-5'>
              <div>
                <Label className='text-[#20272A]/70'>Project Description</Label>
                <Textarea
                  className='mt-2 min-h-[140px] rounded-xl border-[#20272A]/15 bg-white text-[#20272A] placeholder:text-[#20272A]/35 focus-visible:ring-[#B88A3B]'
                  value={form.projectDescription}
                  onChange={(e) => set('projectDescription', e.target.value)}
                  placeholder='Tell us about the site, scope of work, rock/soil conditions, and any specific requirements.'
                  data-testid='enquiry-description'
                />
              </div>
              <div>
                <Label className='text-[#20272A]/70'>Upload Project Brief (optional)</Label>
                <label className='mt-2 flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-[#20272A]/25 bg-[#F6F5F1] px-4 py-5 transition-colors hover:border-[#B88A3B]' data-testid='enquiry-file-upload'>
                  <span className='flex h-10 w-10 items-center justify-center rounded-lg bg-[#075B61]/10 text-[#075B61]'>
                    {fileName ? <FileText size={18} /> : <Upload size={18} />}
                  </span>
                  <span className='text-sm text-[#20272A]/70'>
                    {fileName || 'PDF, image, doc, dwg or zip — up to your file'}
                  </span>
                  <input type='file' className='hidden' onChange={onFile} accept='.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.dwg,.zip' />
                </label>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className='rounded-xl bg-[#F6F5F1] p-6'>
              <h4 className='eyebrow text-[#075B61]'>Review</h4>
              <dl className='mt-4 grid gap-3 sm:grid-cols-2'>
                {[
                  ['Name', form.name], ['Company', form.company], ['Email', form.email], ['Phone', form.phone],
                  ['Location', form.projectLocation], ['Service', form.requiredService], ['Timeline', form.timeline],
                  ['Attachment', fileName],
                ].map(([k, v]) => (
                  <div key={k} className='flex flex-col'>
                    <dt className='text-[11px] uppercase tracking-wider text-[#20272A]/40'>{k}</dt>
                    <dd className='text-sm text-[#20272A]'>{v || <span className='text-[#20272A]/35'>—</span>}</dd>
                  </div>
                ))}
              </dl>
              {form.projectDescription && (
                <div className='mt-4'>
                  <dt className='text-[11px] uppercase tracking-wider text-[#20272A]/40'>Description</dt>
                  <dd className='mt-1 text-sm text-[#20272A]/80'>{form.projectDescription}</dd>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className='mt-8 flex items-center justify-between'>
        <button
          onClick={back}
          disabled={step === 0}
          data-testid='enquiry-back-button'
          className={`inline-flex items-center gap-2 text-sm font-medium transition-colors ${
            step === 0 ? 'cursor-not-allowed text-[#20272A]/25' : 'text-[#20272A]/70 hover:text-[#075B61]'
          }`}
        >
          <ArrowLeft size={16} /> Back
        </button>
        {step < STEPS.length - 1 ? (
          <button
            onClick={next}
            data-testid='enquiry-next-button'
            className='inline-flex h-12 items-center gap-2 rounded-xl bg-[#075B61] px-6 text-sm font-medium text-white transition-colors hover:bg-[#0a6d74]'
          >
            Continue <ArrowRight size={16} />
          </button>
        ) : (
          <button
            onClick={submit}
            disabled={submitting}
            data-testid='enquiry-submit-button'
            className='inline-flex h-12 items-center gap-2 rounded-xl bg-[#B88A3B] px-6 text-sm font-medium text-[#20272A] transition-colors hover:bg-[#c99a48] disabled:opacity-60'
          >
            {submitting ? 'Sending…' : 'Request a Project Discussion'} <ArrowRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
};
